import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArticleHeader } from "./ArticleHeader";
import { getArticle } from "@/services/Articles/getArticle";
import { Article, updateArticle } from "@/services/Articles/updateArticle";
import { Skeleton, Button, message, Modal, Input } from "antd";
import { ArticleActions } from "./ArticleActions";
import "@/pages/Read/css/style.css";
import { deleteArticle } from "@/services/Articles/deleteArticle";
import { SubtitlesCollapse } from "./SubtitlesCollapse";
import { Featured } from "./Featured";
import { RelatedArticles } from "./RelatedArticles";
import { ArticleUpdateForm } from "./ArticleUpdateForm";
import { getSmartStorageItem } from "@/utils/smartStorage";
import { ArticleContent } from "./ArticleContent";

export interface ReadingStatsI {
    words: string,
    readingTime: string
}

const Read = () => {
    const { slug } = useParams<{ slug: string }>();
    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [messageApi, contextHolder] = message.useMessage();
    const role = getSmartStorageItem("role");

    useEffect(() => {
        const fetchArticle = async () => {
            if (!slug) return;
            try {
                setLoading(true);
                const token = localStorage.getItem("token") || undefined;
                const data = await getArticle(slug, token);
                setArticle(data);
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [slug]);

    const handleDelete = async () => {
        if (confirmText.toUpperCase() === "SİLEBİLİRSİN") {
            try {
                const success = await deleteArticle(article!.id);
                if (success) {
                    messageApi.success("Makale silindi!");
                    setIsDeleteModalOpen(false);
                    setConfirmText("");
                } else {
                    messageApi.error("Silme işlemi başarısız!");
                }
            } catch (error) {
                messageApi.error("Silme sırasında hata oluştu!");
                console.error(error);
            }
        } else {
            messageApi.error("Onay metni yanlış! Lütfen 'SİLEBİLİRSİN' yazın.");
        }
    };

    const handleUpdate = async () => {
        try {
            await updateArticle(article!);
            messageApi.success("Makale güncellendi!");
            setIsEditMode(false);
        } catch {
            messageApi.error("Güncelleme başarısız!");
        } finally {
            setIsUpdateModalOpen(false);
        }
    };


    if (!article && !loading) return <p>Blog bulunamadı</p>;
    const toggleEdit = () => setIsEditMode(prev => !prev);
    const content = article?.content ?? "";
    const stats = getStatsFromHtml(content);
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const h2NodeList = doc.querySelectorAll("h2");
    const h2Elements = Array.from(h2NodeList).map(el => el.textContent ?? "");
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "var(--color-bg)",
            }}
        >
            {contextHolder}
            {!loading ? (
                <div
                    className="read-layout-grid"
                >
                    {!isEditMode ? (
                        <>
                            <ArticleHeader
                                className="article-header"
                                slug={article!.slug}
                                imgUrl={article!.imgUrl}
                                title={article!.title}
                                category={article!.category}
                                updatedDate={article!.updatedDate}
                                createdDate={article!.createdDate}
                                isPinned={article!.isPinned}
                                wordStats={stats}
                            />
                            <ArticleActions className="article-actions" />
                            <ArticleContent className="article-content-div" content={article!.content} />
                            <Featured className="article-featured" featured={article?.featured ?? []} />
                            <RelatedArticles className="related-articles" relatedArticlesData={article?.relatedArticlesData ?? []} />
                            < SubtitlesCollapse
                                className="read-subtitles"
                                headings={h2Elements}
                                activeIndex={1}
                            />
                        </>
                    ) : (
                        <ArticleUpdateForm
                            className="article-update-form"
                            article={article!}
                            onSubmit={(formData) => {
                                setArticle(prev => prev ? { ...prev, ...formData } : prev);
                            }}
                        />
                    )}
                </div>
            ) : (<ReadSkeleton />)
            }
            {role === "admin" &&
                (
                    <div
                        style={{
                            backgroundColor: 'grey',
                            alignItems: 'center',
                            borderRadius: '10px',
                            padding: '5px',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '5px',
                            position: 'fixed',
                            bottom: '10px',
                            left: '10px',
                            zIndex: 50,
                            maxWidth: '300px',
                        }}
                    >
                        <Button
                            type="dashed"
                            onClick={toggleEdit}>
                            {isEditMode ? "Görüntüle" : "Düzenle"}
                        </Button>
                        <Button type="primary" danger onClick={() => setIsDeleteModalOpen(true)}>
                            DB SİL
                        </Button>

                        <Button type="primary" onClick={() => setIsUpdateModalOpen(true)}>
                            DB GÜNCELLE
                        </Button>
                        <Modal
                            title="Silme işlemini onayla"
                            open={isDeleteModalOpen}
                            onOk={handleDelete}
                            onCancel={() => {
                                setIsDeleteModalOpen(false);
                                setConfirmText("");
                            }}
                            okText="Sil"
                            okButtonProps={{ danger: true }}
                            cancelText="İptal"
                        >
                            <p>Bu makaleyi silmek istediğine emin misin?</p>
                            <p style={{ margin: '10px 0px' }}
                            >Lütfen onaylamak için 'SİLEBİLİRSİN' yazın:</p>
                            <Input
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="SİLEBİLİRSİN"
                            />
                        </Modal>
                        <Modal
                            title="Güncelleme işlemini onayla"
                            open={isUpdateModalOpen}
                            onOk={handleUpdate}
                            onCancel={() => setIsUpdateModalOpen(false)}
                            okText="Evet, güncelle"
                            cancelText="İptal"
                        >
                            Bu makaleyi güncellemek istediğine emin misin?
                        </Modal>
                    </div>
                )
            }
        </div >
    );
};

export default Read;

const ReadSkeleton = () => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: '800px',
                padding: '10px',
            }}
        >
            <Skeleton.Image
                style={{ display: 'flex', width: "100%", height: 300, borderRadius: 12, marginBottom: '10px', }}
                active
            />
            <Skeleton
                active
                paragraph={{ rows: 6 }}
                title={{ width: "60%" }}
            />
        </div>
    );
}

function getStatsFromHtml(html: string, wpm: number = 200) {
    // 1️⃣ HTML taglerini temizle
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || "";

    // 2️⃣ Kelime sayısı
    const wordsArray = text.trim().split(/\s+/); // boşluk/tab/newline ile ayır
    const wordCount = wordsArray.filter(Boolean).length;

    // 3️⃣ Okuma süresi (dakika)
    const readingTime = Math.ceil(wordCount / wpm);

    return {
        words: wordCount.toString(),
        readingTime: readingTime.toString(),
    };
}
